import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { FilePicker } from 'src/shared/providers/file-picker/file-picker';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { ApplyProjectionResult, ProjectionImportService } from 'src/shared/services/projection-import.service';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { TranslationPipe } from 'src/tidy-ui-components/pipes/translation.pipe';

import {
  IncomeEstimateParseError,
  ParsedIncomeEstimate,
  parseIncomeEstimatePdf,
} from 'src/shared/util/income-estimate-pdf-parser';
import {
  isReconciled,
  projectedNetOperatingIncomeCents,
  STR_MONTHLY_WEIGHTS,
} from 'src/shared/util/income-estimate-projection-rules';

interface UploadProjectionsDialogData {
  addressId: number;
  addressLabel: string;
  /** First month of the 12-month projection window (1-12). */
  startMonth: number;
  startYear: number;
}

type Status = 'idle' | 'parsing' | 'preview' | 'applying' | 'done' | 'error';

/** Which step failed — drives the error screen's wording and retry action. */
type ErrorPhase = 'parse' | 'apply';

interface Outcome {
  /** success = cells written; noop = everything already had a value; empty = no values to write. */
  kind: 'success' | 'noop' | 'empty';
  icon: string;
  title: string;
  detail: string;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

@Component({
  selector: 'tidy-upload-projections-modal',
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, TranslationPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './upload-projections-modal.component.html',
  styleUrls: ['./upload-projections-modal.component.scss'],
})
export class UploadProjectionsModalComponent {
  private readonly filePicker = inject(FilePicker);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly projectionImport = inject(ProjectionImportService);

  /** Populated by the dynamic-dialog-container after construction (before ngOnInit). */
  data: UploadProjectionsDialogData = { addressId: 0, addressLabel: 'Property', startMonth: 1, startYear: new Date().getFullYear() };

  readonly status = signal<Status>('idle');
  readonly parsed = signal<ParsedIncomeEstimate | null>(null);
  readonly errorMessage = signal<string>('');
  readonly errorPhase = signal<ErrorPhase>('parse');
  readonly outcome = signal<Outcome | null>(null);
  /** True while a file is being dragged over the drop zone (for styling). */
  readonly isDragOver = signal<boolean>(false);
  /** Whether the "what to expect" example is expanded. */
  readonly showExample = signal<boolean>(false);

  /**
   * The seasonality curve as relative bar heights (0-100%), Jan…Dec — the same
   * weights the importer uses, so the example mirrors the real distribution.
   */
  readonly exampleMonths = MONTHS_SHORT.map((label, i) => ({
    label,
    pct: Math.round((STR_MONTHLY_WEIGHTS[i] / Math.max(...STR_MONTHLY_WEIGHTS)) * 100),
  }));

  /** Formatted preview rows — computed once per parse rather than every render. */
  readonly preview = computed(() => {
    const p = this.parsed();
    if (!p) {
      return null;
    }
    return {
      gross: this.formatCents(p.grossRevenueAnnualCents),
      expenses: p.expenses.map((e) => ({ id: e.id, label: e.label, amount: this.formatCents(e.annualCents) })),
      noi: this.formatCents(projectedNetOperatingIncomeCents(p)),
      // Warn (don't block) when the parsed figures don't add up to the PDF's
      // stated net — a sign the PDF is a layout we don't fully recognize.
      reconciles: isReconciled(p),
      statedNoi: p.noiAnnualCents === undefined ? null : this.formatCents(p.noiAnnualCents),
    };
  });

  /** "Jul 2026 – Jun 2027" for the 12-month window starting at startMonth/startYear. */
  readonly windowLabel = computed<string>(() => {
    const startIdx = this.data.startMonth - 1;
    const endIdx = (startIdx + 11) % 12;
    const endYear = this.data.startYear + Math.floor((startIdx + 11) / 12);
    return `${MONTHS_SHORT[startIdx]} ${this.data.startYear} – ${MONTHS_SHORT[endIdx]} ${endYear}`;
  });

  /** Arrow wrappers for tidy-button [action] (awaited, with its own spinner). */
  readonly selectFileAction = (): Promise<void> => this.selectFile();
  readonly applyAction = (): Promise<void> => this.apply();
  readonly closeAction = (): void => this.close();

  async selectFile(): Promise<void> {
    let dataUrl: string;
    try {
      const picked = await this.filePicker.pickFile('application/pdf');
      dataUrl = picked.dataUrl;
    } catch {
      // Cancelled — stay on the current step.
      return;
    }
    await this.parseBytes(this.dataUrlToBytes(dataUrl));
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) {
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.errorMessage.set('That doesn’t look like a PDF. Please drop a TIDY income estimate PDF.');
      this.status.set('error');
      return;
    }
    await this.parseBytes(new Uint8Array(await file.arrayBuffer()));
  }

  /** Parse PDF bytes (from a picker selection or a drop) and show the preview. */
  private async parseBytes(bytes: Uint8Array): Promise<void> {
    this.status.set('parsing');
    try {
      const parsed = await parseIncomeEstimatePdf(bytes);
      this.parsed.set(parsed);
      this.status.set('preview');
    } catch (error) {
      this.errorPhase.set('parse');
      this.errorMessage.set(
        error instanceof IncomeEstimateParseError
          ? error.message
          : "We couldn't read this PDF. Make sure it's a TIDY income estimate, then try again.",
      );
      this.status.set('error');
    }
  }

  async apply(): Promise<void> {
    const parsed = this.parsed();
    if (!parsed) {
      return;
    }
    this.status.set('applying');
    try {
      const result = await this.projectionImport.applyProjection(
        parsed,
        this.data.addressId,
        this.data.startYear,
        this.data.startMonth,
      );
      this.outcome.set(this.buildOutcome(result));
      this.status.set('done');
    } catch (error) {
      // Surface our own abort reasons (plain Errors, e.g. the preflight-read
      // failure); fall back to a generic message for backend/HTTP failures.
      this.errorPhase.set('apply');
      this.errorMessage.set(
        error instanceof Error
          ? error.message
          : "We couldn't save these projections. Please try again.",
      );
      this.status.set('error');
    }
  }

  /** Turn the apply result into a clear, human result screen. */
  private buildOutcome(result: ApplyProjectionResult): Outcome {
    const where = `${this.data.addressLabel} · ${this.windowLabel()}`;
    const cells = (n: number): string => `${n} ${n === 1 ? 'value' : 'values'}`;

    if (result.applied > 0) {
      const kept =
        result.skipped > 0
          ? ` We left ${result.skipped} ${result.skipped === 1 ? 'cell you’d' : 'cells you’d'} already edited untouched.`
          : '';
      return {
        kind: 'success',
        icon: 'assets/svg/checkmark-circle-outline.svg',
        title: 'Projections imported',
        detail: `Added ${cells(result.applied)} of projected P&L for ${where}.${kept}`,
      };
    }

    if (result.skipped > 0) {
      return {
        kind: 'noop',
        icon: 'assets/svg/information-circle-outline.svg',
        title: 'Nothing to import',
        detail: `Every projected cell for ${where} already has a value, so nothing changed. Clear those cells first if you want to replace them with this estimate.`,
      };
    }

    return {
      kind: 'empty',
      icon: 'assets/svg/information-circle-outline.svg',
      title: 'Nothing to import',
      detail: `We couldn't find any figures to apply for ${where}.`,
    };
  }

  toggleExample(): void {
    this.showExample.update((v) => !v);
  }

  reset(): void {
    this.parsed.set(null);
    this.errorMessage.set('');
    this.outcome.set(null);
    this.status.set('idle');
  }

  close(): void {
    this.rightSidePanelService.closeRightSidePanel();
  }

  private formatCents(cents: number): string {
    const dollars = Math.abs(cents) / 100;
    const sign = cents < 0 ? '-' : '';
    return `${sign}$${dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }

  private dataUrlToBytes(dataUrl: string): Uint8Array {
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
